"use client";
import { TechQueryResult } from "@/sanity.types";
import { PortableToBlue } from "../../../utility/richTextComponents/richTextComponents";
import { useEffect, useState } from "react";
import Image from "next/image";
import { urlFor } from "@/sanity/lib/image";
import { useKeenSlider } from "keen-slider/react";
import Illustration from "./Illustration/Illustration";

const Header = ({ moduleData }: { moduleData: TechQueryResult["header"]; lng: string }) => {
	const [activeSlideIndex, setActiveSlideIndex] = useState<number>(0);
	const [sliderRef, instanceRef] = useKeenSlider({
		loop: true,
		drag: false,
		slides: {
			spacing: 20,
		},
		// slideChanged(s) {
		// 	setActiveSlideIndex(s.track.details.rel);
		// },
	});
	const [sliderRefMobile, instanceRefMobile] = useKeenSlider({
		loop: true,
		drag: false,
		slides: {
			spacing: 20,
		},
		// slideChanged(s) {
		// 	setActiveSlideIndex(s.track.details.rel);
		// },
	});

	useEffect(() => {
		instanceRef.current?.moveToIdx(activeSlideIndex);
		instanceRefMobile.current?.moveToIdx(activeSlideIndex);
	}, [activeSlideIndex]);

	return (
		<section className="relative z-10 mx-grid-s mt-28 flex max-w-full rounded-[1.25rem] bg-gray-100 p-14 sm:mx-grid-s sm:mt-32 sm:flex-col xs:mx-grid-xs xs:mt-20 xs:p-5">
			<div className="flex w-1/2 flex-col sm:w-full">
				<div className="flex flex-col gap-y-8">
					<h1 className="text-h1 sm:text-h2 xs:text-h1-xs">
						<PortableToBlue value={moduleData?.title} />
					</h1>
					<div className="w-8/12 pb-8 sml:w-10/12 sm:w-full">
						<p className="text-base text-gray-700">{moduleData?.subtitle}</p>
					</div>
				</div>
				<div className="flex h-80 w-8/12 flex-col overflow-hidden border-t-2 border-gray-200 sml:w-10/12 sm:hidden">
					<div ref={sliderRef} className="keen-slider grow">
						{moduleData?.hoverItems?.map((item, index) => (
							<div className="keen-slider__slide" key={index}>
								<div className="flex flex-col gap-y-5 py-5">
									<div className="flex h-11 items-center gap-x-6">
										<div className="relative aspect-square h-full">
											<Image src={urlFor(item.icon).url()} fill alt={item.title?.toLowerCase() || ""} />
										</div>
										<p className="font-secondary">{item.title}</p>
									</div>
									<p className="font-secondary text-base-s text-gray-700">{item.description?.text}</p>
								</div>
								<div className="flex gap-x-1">
									{item.description?.images?.map((image, index) => (
										<div
											key={index}
											className="relative flex h-10 w-20 items-center justify-center gap-x-5 rounded-[0.46rem] bg-white p-1"
										>
											<div className="relative h-full w-[60%]">
												<Image
													src={urlFor(image).url()}
													fill
													alt={item.title?.toLowerCase() || ""}
													className="object-contain"
												/>
											</div>
										</div>
									))}
								</div>
							</div>
						))}
					</div>
					<div className="flex w-full justify-end gap-x-[0.3rem]">
						<div
							className="flex aspect-square h-11 cursor-pointer items-center justify-center rounded-full bg-gray-300 font-secondary text-white duration-300 hover:bg-gray-600"
							onClick={() => {
								instanceRef.current?.prev();
								setActiveSlideIndex(
									activeSlideIndex - 1 < 0 ? (moduleData?.hoverItems?.length ?? 0) - 1 : activeSlideIndex - 1,
								);
							}}
						>
							←
						</div>
						<div
							className="flex aspect-square h-11 cursor-pointer items-center justify-center rounded-full bg-gray-300 font-secondary text-white duration-300 hover:bg-gray-600"
							onClick={() => {
								instanceRef.current?.next();
								setActiveSlideIndex(
									activeSlideIndex + 1 > (moduleData?.hoverItems?.length ?? 0) - 1 ? 0 : activeSlideIndex + 1,
								);
							}}
						>
							→
						</div>
					</div>
				</div>
			</div>
			<Illustration
				hoverItems={moduleData?.hoverItems}
				activeSlideIndex={activeSlideIndex}
				setActiveSlideIndex={setActiveSlideIndex}
			/>
			{/* Mobile slider */}
			<div className="mt-10 hidden min-h-48 w-full flex-col overflow-hidden border-t-2 border-gray-200 sm:flex">
				<div ref={sliderRefMobile} className="keen-slider grow">
					{moduleData?.hoverItems?.map((item, index) => (
						<div className="keen-slider__slide" key={index}>
							<div className="flex flex-col gap-y-5 py-5">
								<div className="flex h-11 items-center gap-x-6">
									<div className="relative aspect-square h-full">
										<Image src={urlFor(item.icon).url()} fill alt={item.title?.toLowerCase() || ""} />
									</div>
									<p className="font-secondary">{item.title}</p>
								</div>
								<p className="font-secondary text-base-s text-gray-700">{item.description?.text}</p>
							</div>
							<div className="flex gap-x-1">
								{item.description?.images?.map((image, index) => (
									<div
										key={index}
										className="relative flex h-10 w-20 items-center justify-center gap-x-5 rounded-[0.46rem] bg-white p-1"
									>
										<div className="relative h-full w-[60%]">
											<Image
												src={urlFor(image).url()}
												fill
												alt={item.title?.toLowerCase() || ""}
												className="object-contain"
											/>
										</div>
									</div>
								))}
							</div>
						</div>
					))}
				</div>
				<div className="mt-8 flex w-full justify-between gap-x-[0.3rem]">
					<div
						className="flex aspect-square h-11 cursor-pointer items-center justify-center rounded-full bg-gray-300 font-secondary text-white duration-300 hover:bg-gray-600"
						onClick={() => {
							instanceRef.current?.prev();
							setActiveSlideIndex(
								activeSlideIndex - 1 < 0 ? (moduleData?.hoverItems?.length ?? 0) - 1 : activeSlideIndex - 1,
							);
						}}
					>
						←
					</div>
					<div
						className="flex aspect-square h-11 cursor-pointer items-center justify-center rounded-full bg-gray-300 font-secondary text-white duration-300 hover:bg-gray-600"
						onClick={() => {
							instanceRef.current?.next();
							setActiveSlideIndex(
								activeSlideIndex + 1 > (moduleData?.hoverItems?.length ?? 0) - 1 ? 0 : activeSlideIndex + 1,
							);
						}}
					>
						→
					</div>
				</div>
			</div>
		</section>
	);
};

export default Header;
